import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Close, DeleteOutline } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import DateInputField from "./DateInputField";
import RaceAssociationPicker from "./raceAssociationPicker";
import CoachDetailModal from "./coachDetailModal";
import TrainingEssentialDetailModal from "./trainingEssentialDetailModal";
import FuelYourRaceDetailModal from "./fuelYourRaceDetailModal";

const DISTANCE_TYPES = ["Half", "Full", "Olympic", "Sprint", "Marathon", "Half Marathon", "Gran Fondo", "Century", "Ultra", "10K"];
const REGIONS = ["North America", "Europe", "Asia", "Oceania", "South America", "Africa", "Middle East"];
const RACE_TYPES = ["Triathlon", "Marathon", "Cycling", "Trail Run", "Swim"];
const TRACK_TYPES = ["Road", "Trail", "Mixed", "Open Water"];
const HOTEL_OPTIONS = ["Nearest", "Lowest Total", "Top Rated"];
const MAX_RACE_DEALS = 3;
const MAX_FEATURED_FUEL_PLANS = 2;

const sanitizeRateToken = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const getRateSupplierName = (rate = {}) =>
  rate.supplierName || rate.providerName || rate.provider?.name || rate.provider || rate.supplier?.name || rate.supplier || "Unknown Supplier";
const buildRateOptionId = ({ planId, refundLabel, supplierName, rateId }) =>
  [planId || "plan", refundLabel || "refund", supplierName || "unknown-supplier", rateId || "rate"]
    .map((part) => sanitizeRateToken(part))
    .join("__");
const normalizeRefundability = (rate = {}) => {
  const raw = String(rate?.refundability || "").toLowerCase();
  if (raw.includes("refund")) {
    return raw.includes("non") ? "Non-refundable" : "Refundable";
  }
  if (rate?.refundable === true) return "Refundable";
  return "Non-refundable";
};
const ratesForRoom = (roomsAndRates, groupId) => (roomsAndRates || []).filter((r) => r.groupId === groupId);
const groupedRatePlansForRoom = (roomsAndRates, roomTypeId) => {
  const candidates = roomTypeId ? ratesForRoom(roomsAndRates, roomTypeId) : (roomsAndRates || []);
  if (candidates.length === 0) return [];
  const grouped = new Map();
  candidates.forEach((r) => {
    const planId = r.boardBasisType || r.boardBasis?.type;
    if (!planId) return;
    const refundLabel = normalizeRefundability(r);
    const supplierName = getRateSupplierName(r);
    const rateId = r.id || r.recommendationId || "";
    const total = parseFloat(r.totalBookingAmount || 0);
    if (!grouped.has(planId)) {
      grouped.set(planId, { planId, name: r.boardBasisName || r.boardBasis?.description || "Room Only", cheapest: new Map() });
    }
    const current = grouped.get(planId);
    const existing = current.cheapest.get(refundLabel);
    if (!existing || total < parseFloat(existing.totalBookingAmount || 0)) {
      const optionId = buildRateOptionId({ planId, refundLabel, supplierName, rateId });
      current.cheapest.set(refundLabel, {
        optionId,
        refundLabel,
        supplierName,
        rateId,
        totalBookingAmount: r.totalBookingAmount || "0.00",
        baseRate: r.baseRate || "0.00",
        taxesAndFees: r.taxesAndFees || "0.00",
      });
    }
  });
  return Array.from(grouped.values()).map((plan) => ({ planId: plan.planId, name: plan.name, options: Array.from(plan.cheapest.values()) }));
};

const COACH_TYPE_OPTIONS = [
  { value: "coach", label: "Coaches" },
  { value: "club", label: "Clubs" },
  { value: "host", label: "Hosts" },
];
const TRAINING_ESSENTIAL_TYPE_OPTIONS = ["Nutrition", "Hydration", "Meal Prep", "Recovery", "Apparel"].map((type) => ({
  value: type,
  label: type,
}));

const blankHotel = {
  hotelId: "",
  startDate: "",
  endDate: "",
  hotelName: "",
  hotelLocation: "",
  heroImage: "",
  overview: "",
  facilities: [],
  selectedFacilities: [],
  sortOptions: [],
  location: { address: "", latitude: "", longitude: "" },
  heroImages: [],
  hasFetched: false,
  roomTypes: [],
  selectedRoomTypeId: "",
  roomTypeName: "",
  roomsAndRates: [],
  selectedRatePlanId: "",
  ratePlanName: "",
  selectedRateOptionId: "",
};

const blankRace = {
  raceName: "",
  heroImage: null,
  eventLink: "",
  startDate: "",
  endDate: "",
  meter: "",
  distanceType: "",
  location: { address: "", latitude: "", longitude: "" },
  region: "",
  numberOfAthletes: "",
  spots_left: "",
  race_type: "",
  track_type: "",
  isSoldOut: false,
  causePercentage: "1.05",
  // Every hotel in a racecation is booked for the exact same travel window — there is
  // no scenario where one hotel has different check-in/check-out dates than another.
  // These two fields are the single source of truth; each hotel entry's own
  // startDate/endDate (used for fetching and for the saved payload) always mirrors them.
  hotelStartDate: "",
  hotelEndDate: "",
  hotels: [{ ...blankHotel }],
};

const toDateInputString = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const todayString = () => new Date().toISOString().split("T")[0];

const RaceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const googleKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const [race, setRace] = useState(blankRace);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [coachActiveMembers, setCoachActiveMembers] = useState({});
  const [uncheckConfirm, setUncheckConfirm] = useState({ open: false, coachId: null, coachName: "", count: 0 });
  const [selectedTrainingEssentials, setSelectedTrainingEssentials] = useState([]);
  const [selectedFuelYourRace, setSelectedFuelYourRace] = useState([]);
  const [featuredFuelYourRace, setFeaturedFuelYourRace] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [detailModal, setDetailModal] = useState({ type: null, record: null });
  const [errors, setErrors] = useState({});
  const [fetchingHotelIndex, setFetchingHotelIndex] = useState(-1);
  const [placeOptions, setPlaceOptions] = useState([]);
  const [placesReady, setPlacesReady] = useState(false);

  const mainEndDateRef = useRef(null);
  const hotelEndDateRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autoServiceRef = useRef(null);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    let url;
    if (race.heroImage instanceof File) {
      url = URL.createObjectURL(race.heroImage);
      setImagePreview(url);
    } else {
      setImagePreview(race.heroImage || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [race.heroImage]);

  useEffect(() => {
    if (!googleKey) {
      setPlacesReady(false);
      return;
    }

    const initGoogle = () => {
      if (!window.google?.maps?.places) return;
      autoServiceRef.current = new window.google.maps.places.AutocompleteService();
      const mapNode = document.createElement("div");
      placesServiceRef.current = new window.google.maps.places.PlacesService(mapNode);
      setPlacesReady(true);
    };

    if (window.google?.maps?.places) {
      initGoogle();
      return;
    }

    const scriptId = "google-places-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      script.onerror = () => toast.error("Google Places failed to load. Please check the API key and allowed domains.");
      document.body.appendChild(script);
    } else {
      const waitForGoogle = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(waitForGoogle);
          initGoogle();
        }
      }, 250);
      return () => clearInterval(waitForGoogle);
    }
  }, [googleKey]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}race/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        setCoachActiveMembers(res?.data?.coachActiveMembers || {});
        const mappedHotels = Array.isArray(data.hotels) && data.hotels.length
          ? data.hotels.map((h) => ({
            ...blankHotel,
            ...h,
            startDate: toDateInputString(h.startDate),
            endDate: toDateInputString(h.endDate),
            selectedFacilities: Array.isArray(h.facilities) ? h.facilities : [],
            heroImages: h.heroImage ? [h.heroImage] : [],
            roomTypes: [],
            selectedRoomTypeId: h.roomTypeId || "",
            roomTypeName: h.roomTypeName || "",
            roomsAndRates: [],
            selectedRatePlanId: h.ratePlanId || "",
            ratePlanName: h.ratePlanName || "",
            selectedRateOptionId: "",
            hasFetched: true,
          }))
          : [{ ...blankHotel }];
        // Every hotel shares one travel window — take the first hotel's saved dates as
        // the canonical value and force every hotel entry to match it, in case any
        // legacy record has mismatched per-hotel dates from before this was enforced.
        const sharedHotelStartDate = mappedHotels[0]?.startDate || "";
        const sharedHotelEndDate = mappedHotels[0]?.endDate || "";
        const syncedHotels = mappedHotels.map((h) => ({ ...h, startDate: sharedHotelStartDate, endDate: sharedHotelEndDate }));
        setIsActive(data.is_active !== undefined ? Boolean(data.is_active) : true);
        setRace({
          raceName: data.raceName || "",
          heroImage: data.heroImage || null,
          eventLink: data.eventLink || "",
          startDate: toDateInputString(data.startDate),
          endDate: toDateInputString(data.endDate),
          meter: String(data.meter ?? ""),
          distanceType: data.distanceType || "",
          location: data.location || { address: "", latitude: "", longitude: "" },
          region: data.region || "",
          numberOfAthletes: String(data.numberOfAthletes ?? ""),
          spots_left: String(data.spots_left ?? ""),
          race_type: data.race_type || "",
          track_type: data.track_type || data.trackType || "",
          isSoldOut: Boolean(data.isSoldOut),
          causePercentage: "1.05", // Fixed site-wide rate — ignore any stored per-race value
          hotelStartDate: sharedHotelStartDate,
          hotelEndDate: sharedHotelEndDate,
          hotels: syncedHotels,
        });
        const existingCoachIds = Array.isArray(data.coaches) ? data.coaches.map((coach) => coach._id || coach) : [];
        const existingTrainingEssentialIds = Array.isArray(data.trainingEssentials)
          ? data.trainingEssentials.map((item) => item._id || item)
          : [];
        const existingFuelYourRaceIds = Array.isArray(data.fuelYourRacePlans)
          ? data.fuelYourRacePlans.map((item) => item._id || item)
          : [];
        const existingFeaturedFuelYourRaceIds = Array.isArray(data.featuredFuelYourRacePlans)
          ? data.featuredFuelYourRacePlans.map((item) => item._id || item)
          : [];
        const existingDealIds = Array.isArray(data.deals)
          ? data.deals.map((item) => item._id || item)
          : [];

        setSelectedCoaches(existingCoachIds);
        setSelectedTrainingEssentials(existingTrainingEssentialIds);
        setSelectedFuelYourRace(existingFuelYourRaceIds);
        setFeaturedFuelYourRace(existingFeaturedFuelYourRaceIds);
        setSelectedDeals(existingDealIds);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, id, isEdit]);

  const totalAmount = 0;
  const causeAmount = useMemo(() => {
    const pct = toFiniteNumber(race.causePercentage) || 0;
    return Number(((totalAmount * pct) / 100).toFixed(2));
  }, [race.causePercentage, totalAmount]);

  // While a saved race cation is active, its coaches / fuel your race / training
  // essentials selections are locked — the admin must deactivate it first.
  const associationsLocked = isEdit && isActive;

  const toggleActive = () => {
    setStatusLoading(true);
    const action = isActive ? "deactivate" : "activate";
    Axios.patch(`${BACKEND_URL}race/${id}/${action}`)
      .then((res) => {
        setIsActive((prev) => !prev);
        toast.success(res?.data?.message || `Race cation ${action}d successfully`);
      })
      .catch(showError)
      .finally(() => setStatusLoading(false));
  };

  const updateRace = (key, value) => {
    setRace((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const updateHotel = (index, key, value) => {
    setRace((prev) => ({
      ...prev,
      hotels: prev.hotels.map((h, i) => (i === index ? { ...h, [key]: value } : h)),
    }));
    setErrors((prev) => ({ ...prev, [`${key}_${index}`]: "" }));
  };

  const updateNumericRace = (key, value) => {
    if (value === "" || (Number.isFinite(Number(value)) && Number(value) >= 0)) {
      updateRace(key, value);
    }
  };

  const toggleHotelFacility = (index, facility) => {
    const hotel = race.hotels[index];
    const selected = hotel.selectedFacilities.includes(facility)
      ? hotel.selectedFacilities.filter((item) => item !== facility)
      : [...hotel.selectedFacilities, facility];
    updateHotel(index, "selectedFacilities", selected);
    setErrors((prev) => ({ ...prev, [`facilities_${index}`]: "" }));
  };

  const handleRoomTypeSelect = (index, roomType) => {
    updateHotel(index, "selectedRoomTypeId", roomType.id);
    updateHotel(index, "roomTypeName", roomType.name);

    const hotel = race.hotels[index];
    const plans = groupedRatePlansForRoom(hotel.roomsAndRates, roomType.id);
    const defaultPlan = plans[0] || null;
    const defaultOption = defaultPlan?.options.find((opt) => opt.refundLabel === "Non-refundable") || defaultPlan?.options[0];
    updateHotel(index, "selectedRatePlanId", defaultPlan?.planId || "");
    updateHotel(index, "ratePlanName", defaultPlan?.name || "");
    updateHotel(index, "selectedRateOptionId", defaultOption?.optionId || "");
  };

  const handleRateSelect = (index, rp) => {
    updateHotel(index, "selectedRatePlanId", rp.planId);
    updateHotel(index, "ratePlanName", rp.name);
    updateHotel(index, "selectedRateOptionId", rp.optionId || "");
  };

  const commitMainStartDate = (value) => {
    setRace((prev) => ({
      ...prev,
      startDate: value,
      endDate: prev.endDate && prev.endDate < value ? "" : prev.endDate,
    }));
    setErrors((prev) => ({ ...prev, startDate: "" }));
  };

  // All hotels in a racecation always share the exact same travel dates — changing
  // either date here propagates to every hotel entry at once.
  const commitHotelStartDate = (value) => {
    setRace((prev) => {
      const nextEndDate = prev.hotelEndDate && prev.hotelEndDate < value ? "" : prev.hotelEndDate;
      return {
        ...prev,
        hotelStartDate: value,
        hotelEndDate: nextEndDate,
        hotels: prev.hotels.map((h) => ({ ...h, startDate: value, endDate: nextEndDate })),
      };
    });
    setErrors((prev) => ({ ...prev, hotelStartDate: "" }));
  };

  const commitHotelEndDate = (value) => {
    setRace((prev) => ({
      ...prev,
      hotelEndDate: value,
      hotels: prev.hotels.map((h) => ({ ...h, endDate: value })),
    }));
    setErrors((prev) => ({ ...prev, hotelEndDate: "" }));
  };

  const addHotel = () => setRace((prev) => ({
    ...prev,
    hotels: [...prev.hotels, { ...blankHotel, startDate: prev.hotelStartDate, endDate: prev.hotelEndDate }],
  }));
  const removeHotel = (index) => setRace((prev) => {
    const next = prev.hotels.filter((_, i) => i !== index);
    return { ...prev, hotels: next.length ? next : [{ ...blankHotel }] };
  });

  const loadPlaceSuggestions = (input) => {
    if (!input || input.trim().length < 2) {
      setPlaceOptions([]);
      return;
    }
    if (!autoServiceRef.current) return;

    autoServiceRef.current.getPlacePredictions({ input, types: ["geocode"] }, (predictions) => {
      setPlaceOptions((predictions || []).map((p) => ({ description: p.description, placeId: p.place_id })));
    });
  };

  const selectPlace = (option) => {
    if (!option?.placeId || !placesServiceRef.current) return;
    placesServiceRef.current.getDetails({ placeId: option.placeId, fields: ["formatted_address", "geometry"] }, (place) => {
      const lat = place?.geometry?.location?.lat?.();
      const lng = place?.geometry?.location?.lng?.();
      updateRace("location", {
        address: place?.formatted_address || option.description,
        latitude: Number.isFinite(lat) ? lat : "",
        longitude: Number.isFinite(lng) ? lng : "",
      });
    });
  };

  const fetchHotel = async (index) => {
    const hotel = race.hotels[index];
    if (!hotel.hotelId || !hotel.startDate || !hotel.endDate) {
      toast.error("Please enter Hotel ID, Start Date and End Date");
      return;
    }
    setFetchingHotelIndex(index);
    try {
      const res = await Axios.get(`${BACKEND_URL}hotel/${hotel.hotelId}/details`, {
        params: { checkIn: hotel.startDate, checkOut: hotel.endDate },
      });
      const data = res?.data?.data || {};
      updateHotel(index, "hotelName", data.hotelName || "");
      updateHotel(index, "hotelLocation", data.destination || data.locationAddress || "");
      updateHotel(index, "overview", data.overview || "");
      updateHotel(index, "facilities", Array.isArray(data.facilities) ? data.facilities : []);
      updateHotel(index, "selectedFacilities", []);
      updateHotel(index, "heroImages", Array.isArray(data.heroImages) ? data.heroImages : []);
      updateHotel(index, "hasFetched", true);
      if (!hotel.heroImage && Array.isArray(data.heroImages) && data.heroImages.length) {
        updateHotel(index, "heroImage", data.heroImages[0]);
      }
      updateHotel(index, "location", {
        address: data.locationAddress || "",
        latitude: data.latitude || "",
        longitude: data.longitude || "",
      });

      const roomTypes = Array.isArray(data.roomTypes) ? data.roomTypes : [];
      const roomsAndRates = Array.isArray(data.roomsAndRates) ? data.roomsAndRates : [];
      updateHotel(index, "roomTypes", roomTypes);
      updateHotel(index, "roomsAndRates", roomsAndRates);
      const matchedRoomType = hotel.roomTypeName
        ? roomTypes.find((rt) => rt.name?.toLowerCase() === hotel.roomTypeName.toLowerCase())
        : null;
      const defaultRoomType = matchedRoomType || roomTypes[0] || null;
      updateHotel(index, "selectedRoomTypeId", defaultRoomType?.id || "");
      updateHotel(index, "roomTypeName", defaultRoomType?.name || "");

      const plans = defaultRoomType ? groupedRatePlansForRoom(roomsAndRates, defaultRoomType.id) : [];
      const matchedPlan = hotel.ratePlanName
        ? plans.find((p) => p.name?.toLowerCase() === hotel.ratePlanName.toLowerCase())
        : null;
      const defaultPlan = matchedPlan || plans[0] || null;
      const defaultOption = defaultPlan?.options.find((opt) => opt.refundLabel === "Non-refundable") || defaultPlan?.options[0];
      updateHotel(index, "selectedRatePlanId", defaultPlan?.planId || "");
      updateHotel(index, "ratePlanName", defaultPlan?.name || "");
      updateHotel(index, "selectedRateOptionId", defaultOption?.optionId || "");

      toast.success("Hotel details loaded");
    } catch (err) {
      showError(err);
    } finally {
      setFetchingHotelIndex(-1);
    }
  };

  const validate = () => {
    const next = {};

    if (!race.raceName.trim()) next.raceName = "Race cation name is required";
    if (!race.startDate) next.startDate = "Start date is required";
    else if (!isEdit && race.startDate < todayString()) {
      next.startDate = "Start date cannot be before today";
    }


    if (!race.endDate) next.endDate = "End date is required";
    else if (race.startDate && race.endDate < race.startDate) next.endDate = "End date cannot be before start date";

    if (!race.location.address.trim()) next.location = "Please choose a location from suggestions";
    if (!String(race.meter).trim() || toFiniteNumber(race.meter) === null || toFiniteNumber(race.meter) < 0) {
      next.meter = "Meter is required and must be numeric and >= 0";
    }
    if (!race.distanceType) next.distanceType = "Distance Type is required";
    if (!race.region) next.region = "Region is required";
    if (!String(race.numberOfAthletes).trim() || toFiniteNumber(race.numberOfAthletes) === null || toFiniteNumber(race.numberOfAthletes) < 0) {
      next.numberOfAthletes = "Number of Athletes is required and must be numeric and >= 0";
    }
    if (toFiniteNumber(race.causePercentage) === null || toFiniteNumber(race.causePercentage) < 0) {
      next.causePercentage = "Cause percentage must be numeric and >= 0";
    }
    if (!String(race.spots_left).trim() || toFiniteNumber(race.spots_left) === null || toFiniteNumber(race.spots_left) < 0) {
      next.spots_left = "Number of Spots Left is required and must be numeric and >= 0";
    }
    if (!race.race_type) next.race_type = "Race Type is required";
    if (!race.track_type) next.track_type = "Track Type is required";
    if (race.eventLink.trim() && !/^https?:\/\/.+/i.test(race.eventLink.trim())) {
      next.eventLink = "Event Link must be a valid URL starting with http:// or https://";
    }

    // All hotels share one travel window — validate it once, not per hotel. Skip entirely
    // only when the form is untouched (a single blank hotel row and no shared dates yet).
    const hasAnyHotelData = race.hotels.some((h) => h.hotelId.trim()) || race.hotelStartDate || race.hotelEndDate;
    if (hasAnyHotelData) {
      // Hotel check-in dates are independent of the race's own start date and of whatever was
      // previously saved — a hotel stay can legitimately begin several days before the race, and
      // an admin can freely move it earlier or later. The only real constraint is "not in the past".
      if (!race.hotelStartDate) next.hotelStartDate = "Start date required";
      else if (race.hotelStartDate < todayString()) next.hotelStartDate = "Cannot be before today";
      if (!race.hotelEndDate) next.hotelEndDate = "End date required";
      else if (race.hotelStartDate && race.hotelEndDate < race.hotelStartDate) next.hotelEndDate = "Cannot be before start date";

      race.hotels.forEach((hotel, i) => {
        if (!hotel.hotelId.trim()) next[`hotelId_${i}`] = "Hotel ID required";
        if (hotel.facilities.length > 0 && hotel.selectedFacilities.length === 0) next[`facilities_${i}`] = "Select at least one facility";
      });
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    setLoading(true);
    const payload = new FormData();
    payload.append("raceName", race.raceName.trim());
    payload.append("eventLink", race.eventLink.trim());
    payload.append("startDate", race.startDate);
    payload.append("endDate", race.endDate);
    payload.append("meter", Number(race.meter || 0));
    payload.append("distanceType", race.distanceType);
    payload.append("location", JSON.stringify(race.location));
    payload.append("region", race.region);
    payload.append("numberOfAthletes", Number(race.numberOfAthletes || 0));
    payload.append("spots_left", Number(race.spots_left || 0));
    payload.append("race_type", race.race_type);
    payload.append("track_type", race.track_type);
    payload.append("isSoldOut", String(Boolean(race.isSoldOut)));
    payload.append("causePercentage", String(toFiniteNumber(race.causePercentage) || 0));
    payload.append("causeAmount", String(causeAmount));
    payload.append("hotels", JSON.stringify(race.hotels.map((hotel) => ({
      hotelId: hotel.hotelId,
      startDate: hotel.startDate,
      endDate: hotel.endDate,
      hotelName: hotel.hotelName,
      hotelLocation: hotel.hotelLocation,
      heroImage: hotel.heroImage,
      overview: hotel.overview,
      facilities: hotel.selectedFacilities,
      sortOptions: hotel.sortOptions,
      location: hotel.location,
      roomTypeId: hotel.selectedRoomTypeId,
      roomTypeName: hotel.roomTypeName,
      ratePlanId: hotel.selectedRatePlanId,
      ratePlanName: hotel.ratePlanName,
    }))));
    payload.append("coaches", JSON.stringify(selectedCoaches));
    payload.append("trainingEssentials", JSON.stringify(selectedTrainingEssentials));
    payload.append("fuelYourRacePlans", JSON.stringify(selectedFuelYourRace));
    payload.append("featuredFuelYourRacePlans", JSON.stringify(featuredFuelYourRace));
    payload.append("deals", JSON.stringify(selectedDeals));
    if (race.heroImage instanceof File) payload.append("heroImage", race.heroImage);

    const request = isEdit
      ? Axios.put(`${BACKEND_URL}race/${id}`, payload, { headers: { "Content-Type": "multipart/form-data" } })
      : Axios.post(`${BACKEND_URL}race`, payload, { headers: { "Content-Type": "multipart/form-data" } });

    request
      .then((res) => {
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/race-management");
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  return (
    <Box className="container-fluid">
      <Helmet><title>{isEdit ? "Edit Race Cation" : "Create Race Cation"}</title></Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>{isEdit ? "Edit Race Cation" : "Create Race Cation"}</h2>
        <Button onClick={() => navigate("/race-management")}>Back</Button>
      </div>

      <Stack spacing={2.5}>
        <Card variant="outlined"><CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Race Cation Name" value={race.raceName} onChange={(e) => updateRace("raceName", e.target.value)} error={Boolean(errors.raceName)} helperText={errors.raceName} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Event Link" placeholder="https://www.example.com/race-event" value={race.eventLink} onChange={(e) => updateRace("eventLink", e.target.value)} error={Boolean(errors.eventLink)} helperText={errors.eventLink || "Direct link to the race/marathon event page"} /></Grid>
            <Grid item xs={12} md={3}>
              <DateInputField
                label="Start Date"
                value={race.startDate}
                min={todayString()}
                onCommit={commitMainStartDate}
                linkedEndInputRef={mainEndDateRef}
                error={Boolean(errors.startDate)}
                helperText={errors.startDate}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DateInputField
                label="End Date"
                value={race.endDate}
                min={race.startDate || todayString()}
                onCommit={(value) => updateRace("endDate", value)}
                inputRef={mainEndDateRef}
                rangeStart={race.startDate}
                error={Boolean(errors.endDate)}
                helperText={errors.endDate}
              />
            </Grid>
            <Grid item xs={12} md={3}><TextField required fullWidth type="number" label="Meter" value={race.meter} onChange={(e) => updateNumericRace("meter", e.target.value)} error={Boolean(errors.meter)} helperText={errors.meter} /></Grid>
            <Grid item xs={12} md={3}><FormControl required fullWidth error={Boolean(errors.distanceType)}><InputLabel>Distance Type</InputLabel><Select label="Distance Type" value={race.distanceType} onChange={(e) => updateRace("distanceType", e.target.value)}>{DISTANCE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</Select>{errors.distanceType && <FormHelperText>{errors.distanceType}</FormHelperText>}</FormControl></Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={placeOptions}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.description || "")}
                filterOptions={(x) => x}
                value={race.location.address ? { description: race.location.address } : null}
                onInputChange={(_, value) => {
                  updateRace("location", { ...race.location, address: value });
                  loadPlaceSuggestions(value);
                }}
                onChange={(_, selected) => selectPlace(selected)}
                renderInput={(params) => <TextField {...params} label="Location (Google Places)" error={Boolean(errors.location)} helperText={errors.location || (googleKey ? placesReady ? "" : "Loading Google Places..." : "Missing REACT_APP_GOOGLE_MAPS_API_KEY")} />}
              />
            </Grid>
            <Grid item xs={12} md={3}><FormControl required fullWidth error={Boolean(errors.region)}><InputLabel>Region</InputLabel><Select label="Region" value={race.region} onChange={(e) => updateRace("region", e.target.value)}>{REGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select>{errors.region && <FormHelperText>{errors.region}</FormHelperText>}</FormControl></Grid>
            <Grid item xs={12} md={3}><TextField required fullWidth type="number" label="Number of Athletes" value={race.numberOfAthletes} onChange={(e) => updateNumericRace("numberOfAthletes", e.target.value)} error={Boolean(errors.numberOfAthletes)} helperText={errors.numberOfAthletes} /></Grid>
            <Grid item xs={12} md={3}><TextField required fullWidth type="number" label="Number of Spots Left" value={race.spots_left} onChange={(e) => updateNumericRace("spots_left", e.target.value)} error={Boolean(errors.spots_left)} helperText={errors.spots_left} /></Grid>
            <Grid item xs={12} md={3}><FormControl fullWidth error={Boolean(errors.race_type)}><InputLabel>Race Type</InputLabel><Select label="Race Type" value={race.race_type} onChange={(e) => updateRace("race_type", e.target.value)}>{RACE_TYPES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select>{errors.race_type && <FormHelperText>{errors.race_type}</FormHelperText>}</FormControl></Grid>
            <Grid item xs={12} md={3}><FormControl fullWidth error={Boolean(errors.track_type)}><InputLabel>Track Type</InputLabel><Select label="Track Type" value={race.track_type} onChange={(e) => updateRace("track_type", e.target.value)}>{TRACK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</Select>{errors.track_type && <FormHelperText>{errors.track_type}</FormHelperText>}</FormControl></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={race.isSoldOut} onChange={(e) => updateRace("isSoldOut", e.target.checked)} />} label="Is Sold Out?" /></Grid>
          </Grid>
        </CardContent></Card>

        <Card variant="outlined"><CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}><TextField type="file" fullWidth inputProps={{ accept: "image/*" }} onChange={(e) => updateRace("heroImage", e.target.files?.[0] || null)} error={Boolean(errors.heroImage)} helperText={errors.heroImage || "Select a hero image"} /></Grid>
            <Grid item xs={12} md={7}>{imagePreview ? <img src={imagePreview} alt="Race cation hero preview" style={{ width: "100%", maxHeight: 250, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} /> : <Typography variant="body2" color="text.secondary"></Typography>}</Grid>
          </Grid>
        </CardContent></Card>

        <Card variant="outlined"><CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Hotels</Typography>
            <Button startIcon={<Add />} onClick={addHotel}>Add Hotel</Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Every hotel below is booked for the same travel dates — set them once here.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <DateInputField
                label="Hotel Start Date"
                value={race.hotelStartDate}
                min={todayString()}
                onCommit={commitHotelStartDate}
                linkedEndInputRef={hotelEndDateRef}
                error={Boolean(errors.hotelStartDate)}
                helperText={errors.hotelStartDate}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DateInputField
                label="Hotel End Date"
                value={race.hotelEndDate}
                min={race.hotelStartDate || todayString()}
                onCommit={commitHotelEndDate}
                inputRef={hotelEndDateRef}
                rangeStart={race.hotelStartDate}
                error={Boolean(errors.hotelEndDate)}
                helperText={errors.hotelEndDate}
              />
            </Grid>
          </Grid>

          <Stack spacing={2}>
            {race.hotels.map((hotel, index) => (
              <Paper key={`hotel-${index}`} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                  <Typography variant="subtitle1">Hotel Entry #{index + 1}</Typography>
                  {race.hotels.length > 1 && <IconButton onClick={() => removeHotel(index)}><DeleteOutline /></IconButton>}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Hotel ID" value={hotel.hotelId} onChange={(e) => updateHotel(index, "hotelId", e.target.value)} error={Boolean(errors[`hotelId_${index}`])} helperText={errors[`hotelId_${index}`]} /></Grid>
                  <Grid item xs={12} md={6}><Button fullWidth variant="contained" onClick={() => fetchHotel(index)} disabled={fetchingHotelIndex === index}>{fetchingHotelIndex === index ? "Loading..." : "Fetch Hotel"}</Button></Grid>

                  {hotel.hasFetched && (
                    <>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Hotel Name" value={hotel.hotelName} InputProps={{ readOnly: true }} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Hotel Location" value={hotel.hotelLocation} InputProps={{ readOnly: true }} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Overview" value={hotel.overview} InputProps={{ readOnly: true }} /></Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Hotel Preference Options</Typography>
                        <FormGroup row>
                          {HOTEL_OPTIONS.map((opt) => (
                            <FormControlLabel
                              key={opt}
                              control={<Checkbox checked={hotel.sortOptions.includes(opt)} onChange={(e) => updateHotel(index, "sortOptions", e.target.checked ? [...hotel.sortOptions, opt] : hotel.sortOptions.filter((x) => x !== opt))} />}
                              label={opt}
                            />
                          ))}
                        </FormGroup>
                      </Grid>

                      {hotel.heroImages.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Hero Image</Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {hotel.heroImages.map((img) => (
                              <img key={img} src={img} alt="hotel hero" onClick={() => updateHotel(index, "heroImage", img)} style={{ width: 90, height: 65, objectFit: "cover", borderRadius: 6, border: hotel.heroImage === img ? "2px solid #1976d2" : "1px solid #ddd", cursor: "pointer" }} />
                            ))}
                          </Box>
                          {hotel.heroImage && <Box sx={{ mt: 1 }}><img src={hotel.heroImage} alt="selected hotel hero" style={{ width: 220, height: 130, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} /></Box>}
                        </Grid>
                      )}

                      {hotel.facilities.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 700 }}>Facilities ({hotel.selectedFacilities.length} selected)</Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 220, overflow: "auto" }}>
                            {hotel.facilities.map((facility) => (
                              <Chip
                                key={facility}
                                label={facility}
                                clickable
                                color={hotel.selectedFacilities.includes(facility) ? "primary" : "default"}
                                variant={hotel.selectedFacilities.includes(facility) ? "filled" : "outlined"}
                                onClick={() => toggleHotelFacility(index, facility)}
                              />
                            ))}
                          </Box>
                          {errors[`facilities_${index}`] && <Typography variant="caption" color="error">{errors[`facilities_${index}`]}</Typography>}
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ mt: 1 }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                              Available Room Types
                              {hotel.selectedRoomTypeId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a room type for this hotel</Typography>
                            {hotel.roomTypes.length === 0 ? (
                              <Typography variant="body2" color="warning.main">No room types found for the selected dates.</Typography>
                            ) : (
                              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
                                {hotel.roomTypes.map((rt, idx) => (
                                  <Box
                                    key={rt.id}
                                    onClick={() => handleRoomTypeSelect(index, rt)}
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                      border: hotel.selectedRoomTypeId === rt.id ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                      backgroundColor: hotel.selectedRoomTypeId === rt.id ? "#ffffff" : "#fafafa",
                                      color: hotel.selectedRoomTypeId === rt.id ? "#1976d2" : "inherit",
                                      "&:hover": { borderColor: "#1976d2", backgroundColor: "#ffffff" },
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{`${idx + 1}. ${rt.name}`}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ mt: 1 }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                              Available Rate Plans
                              {hotel.selectedRatePlanId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {hotel.selectedRoomTypeId ? "Showing rate plans for selected room type" : "Select a room type first to filter rate plans"}
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 2 }}>
                              {groupedRatePlansForRoom(hotel.roomsAndRates, hotel.selectedRoomTypeId).map((plan) => (
                                <Box key={plan.planId} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{plan.name}</Typography>
                                  <RadioGroup
                                    value={hotel.selectedRatePlanId === plan.planId ? hotel.selectedRateOptionId : ""}
                                    onChange={(e) => {
                                      const option = plan.options.find((opt) => opt.optionId === e.target.value);
                                      if (option) {
                                        handleRateSelect(index, { planId: plan.planId, name: plan.name, optionId: option.optionId });
                                      }
                                    }}
                                  >
                                    {plan.options.map((opt) => (
                                      <Box key={`${plan.planId}-${opt.refundLabel}`} sx={{ border: "1px solid #e5e5e5", borderRadius: 1.5, p: 1.5, mb: 1.2, backgroundColor: "#fff" }}>
                                        <FormControlLabel value={opt.optionId} control={<Radio size="small" />} label={opt.refundLabel} sx={{ m: 0 }} />
                                        <Typography variant="body2" color="text.secondary">Supplier: {opt.supplierName || "-"}</Typography>
                                        <Typography variant="body2" color="text.secondary">Total: ${parseFloat(opt.totalBookingAmount || 0).toFixed(2)}</Typography>
                                        <Typography variant="caption" color="text.secondary">Base: ${parseFloat(opt.baseRate || 0).toFixed(2)} | Taxes: ${parseFloat(opt.taxesAndFees || 0).toFixed(2)}</Typography>
                                      </Box>
                                    ))}
                                  </RadioGroup>
                                </Box>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            ))}
          </Stack>
        </CardContent></Card>

        {isEdit && (
          <Card variant="outlined"><CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="subtitle1">Race Cation Status: {isActive ? "Active" : "Inactive"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Coaches, Fuel Your Race, and Training Essentials selections can only be added or removed while this race cation is
                  inactive — reordering Training Essentials and Fuel Your Race items is always allowed.
                </Typography>
              </Box>
              <FormControlLabel
                control={<Switch checked={isActive} disabled={statusLoading} onChange={toggleActive} />}
                label={isActive ? "Active" : "Inactive"}
              />
            </Box>
            {associationsLocked && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This race cation is active. Deactivate it above to add or remove Coaches, Fuel Your Race, or Training Essentials selections
                — you can still drag to reorder Training Essentials and Fuel Your Race items while active.
              </Alert>
            )}
          </CardContent></Card>
        )}

        <Card variant="outlined">
          <CardContent>
            <RaceAssociationPicker
              title="Coaches, Hosts & Clubs"
              endpoint="coach"
              typeOptions={COACH_TYPE_OPTIONS}
              selectedIds={selectedCoaches}
              disabled={associationsLocked}
              onToggle={(id, record) => {
                if (associationsLocked) return;
                const isRemoving = selectedCoaches.includes(id);
                const activeCount = coachActiveMembers[id] || 0;
                if (isRemoving && activeCount > 0) {
                  setUncheckConfirm({ open: true, coachId: id, coachName: record?.coachName || "This coach", count: activeCount });
                  return;
                }
                setSelectedCoaches((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
              }}
              onRowClick={(record) => setDetailModal({ type: "coach", record })}
              getThumbnail={(record) => {
                const img = record.profileimg || record.coachImage;
                return img ? { src: img, alt: record.coachName } : null;
              }}
              columns={[
                { name: "coachName", label: "Name", render: (record) => record.coachName || "-" },
                {
                  name: "type",
                  label: "Type",
                  render: (record) => <Chip label={(record.type || "coach").toUpperCase()} size="small" />,
                },
                { name: "title", label: "Title", render: (record) => record.title || "-" },
                { name: "location", label: "Location", render: (record) => record.location || "-" },
              ]}
            />
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <RaceAssociationPicker
              title="Training Essentials"
              endpoint="training-essentials"
              typeOptions={TRAINING_ESSENTIAL_TYPE_OPTIONS}
              filterVariant="dropdown"
              enableReorder
              avatar
              selectedIds={selectedTrainingEssentials}
              disabled={associationsLocked}
              onToggle={(id) => {
                if (associationsLocked) return;
                setSelectedTrainingEssentials((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
              }}
              onReorder={(ids) => setSelectedTrainingEssentials(ids)}
              onRowClick={(record) => setDetailModal({ type: "trainingEssential", record })}
              getThumbnail={(record) => (record.bannerImage ? { src: record.bannerImage, alt: record.name } : null)}
              columns={[
                { name: "name", label: "Name", render: (record) => record.name || "-" },
                { name: "type", label: "Type", render: (record) => <Chip label={record.type} size="small" /> },
                { name: "price", label: "Price", render: (record) => (record.price !== undefined ? `$${record.price}` : "-") },
                { name: "tag", label: "Tag", render: (record) => record.tag || "-" },
              ]}
            />
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <RaceAssociationPicker
              title="Fuel Your Race Plans"
              endpoint="fuel-your-race"
              typeOptions={TRAINING_ESSENTIAL_TYPE_OPTIONS}
              filterVariant="dropdown"
              enableReorder
              avatar
              selectedIds={selectedFuelYourRace}
              disabled={associationsLocked}
              onToggle={(id) => {
                if (associationsLocked) return;
                setSelectedFuelYourRace((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
                // Unselecting a plan entirely means it can no longer be featured either.
                setFeaturedFuelYourRace((prev) => prev.filter((c) => c !== id));
              }}
              onReorder={(ids) => setSelectedFuelYourRace(ids)}
              onRowClick={(record) => setDetailModal({ type: "fuelYourRace", record })}
              getThumbnail={(record) => (record.profileimg ? { src: record.profileimg, alt: record.planName } : null)}
              featuredIds={featuredFuelYourRace}
              maxFeatured={MAX_FEATURED_FUEL_PLANS}
              onToggleFeatured={(id) => {
                if (associationsLocked) return;
                setFeaturedFuelYourRace((prev) => {
                  if (prev.includes(id)) return prev.filter((c) => c !== id);
                  if (prev.length >= MAX_FEATURED_FUEL_PLANS) {
                    toast.error(`You can feature at most ${MAX_FEATURED_FUEL_PLANS} fuel your race plans`);
                    return prev;
                  }
                  return [...prev, id];
                });
              }}
              columns={[
                { name: "planName", label: "Plan Name", render: (record) => record.planName || "-" },
                { name: "type", label: "Type", render: (record) => (record.type ? <Chip label={record.type} size="small" /> : "-") },
                { name: "subtitle", label: "Subtitle", render: (record) => record.subtitle || "-" },
                { name: "price", label: "Price", render: (record) => (record.price !== undefined ? `$${record.price}` : "-") },
                { name: "couponCode", label: "Coupon", render: (record) => record.couponCode || "-" },
              ]}
            />
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose up to {MAX_RACE_DEALS} deals/itineraries to feature on this race cation's public page.
            </Typography>
            <RaceAssociationPicker
              title="Featured Deals / Itineraries"
              endpoint="package"
              enableReorder
              maxSelected={MAX_RACE_DEALS}
              selectedIds={selectedDeals}
              onToggle={(id) => {
                setSelectedDeals((prev) => {
                  if (prev.includes(id)) return prev.filter((d) => d !== id);
                  if (prev.length >= MAX_RACE_DEALS) {
                    toast.error(`You can feature at most ${MAX_RACE_DEALS} deals on a race cation`);
                    return prev;
                  }
                  return [...prev, id];
                });
              }}
              onReorder={(ids) => setSelectedDeals(ids)}
              onRowClick={() => {}}
              getThumbnail={(record) => (record.image ? { src: record.image, alt: record.title } : null)}
              columns={[
                { name: "title", label: "Title", render: (record) => record.title || "-" },
                { name: "hotel_name", label: "Hotel", render: (record) => record.hotel_name || "-" },
                { name: "destination", label: "Destination", render: (record) => record.destination || "-" },
                { name: "number_of_nights", label: "Nights", render: (record) => record.number_of_nights || "-" },
                { name: "totalBookingAmount", label: "Price", render: (record) => (record.totalBookingAmount ? `$${record.totalBookingAmount}` : "-") },
              ]}
            />
          </CardContent>
        </Card>
      </Stack>

      <CoachDetailModal
        open={detailModal.type === "coach"}
        onClose={() => setDetailModal({ type: null, record: null })}
        record={detailModal.type === "coach" ? detailModal.record : null}
      />
      <TrainingEssentialDetailModal
        open={detailModal.type === "trainingEssential"}
        onClose={() => setDetailModal({ type: null, record: null })}
        record={detailModal.type === "trainingEssential" ? detailModal.record : null}
      />
      <FuelYourRaceDetailModal
        open={detailModal.type === "fuelYourRace"}
        onClose={() => setDetailModal({ type: null, record: null })}
        record={detailModal.type === "fuelYourRace" ? detailModal.record : null}
      />

      <Dialog
        open={uncheckConfirm.open}
        onClose={() => setUncheckConfirm({ open: false, coachId: null, coachName: "", count: 0 })}
        fullWidth
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Remove {uncheckConfirm.coachName}?</h3>
          <IconButton onClick={() => setUncheckConfirm({ open: false, coachId: null, coachName: "", count: 0 })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {uncheckConfirm.count} member{uncheckConfirm.count === 1 ? " is" : "s are"} currently in {uncheckConfirm.coachName}'s chat room
            for this race cation.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            If you remove this coach, here's what happens on the website:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 0 }}>
            <Typography component="li" variant="body2">
              Their group's chat room disappears from this race cation's page and from their "My Trips" view.
            </Typography>
            <Typography component="li" variant="body2">
              They lose access to the chat history and member list for that group.
            </Typography>
            <Typography component="li" variant="body2">
              They still won't be able to join a different group for this trip — their membership stays "active" behind the scenes, and the
              3-day minimum membership rule still applies — so they'll likely need manual support to get unstuck.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setUncheckConfirm({ open: false, coachId: null, coachName: "", count: 0 })}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            color="warning"
            onClick={() => {
              setSelectedCoaches((prev) => prev.filter((c) => c !== uncheckConfirm.coachId));
              setUncheckConfirm({ open: false, coachId: null, coachName: "", count: 0 });
            }}
          >
            Remove anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" disabled={loading} onClick={submit}>{loading ? "Saving..." : isEdit ? "Update" : "Submit"}</Button>
        <Button onClick={() => navigate("/race-management")}>Cancel</Button>
      </Box>
    </Box>
  );
};

export default RaceForm;
